<template>
  <PmStartupPure :is-loading="isLoading">
    <PmStartupNeedsActionPure
      v-if="xstate.state.value.matches('changePassword')"
      message="Bevor es weitergeht, musst du dein Passwort ändern!"
    >
      <PmChangePassword
        :use-timeout="true"
        @success="xstate.service.value.send('PASSWORD_CHANGED')"
      />
    </PmStartupNeedsActionPure>
  </PmStartupPure>
</template>

<script setup lang="ts">
import { onMounted, computed } from 'vue'
import { isNil } from 'lodash-es'
import { BREAKPOINT } from '@/constants/general'
import { addUserAbility } from '@/services/ability'

import { useXState } from '@/composition/useXState'
import { PmStartupState } from '@/components/PmStartup/PmStartupState'
import PmStartupPure from '@/components/PmStartup/PmStartupPure.vue'
import PmStartupNeedsActionPure from '@/components/PmStartup/PmStartupNeedsActionPure.vue'
import PmChangePassword from '@/components/PmChangePassword/PmChangePassword.vue'
import { useApolloClient } from '@vue/apollo-composable'
import {
  CurrentUserDocument,
  type CurrentUserQuery,
  StaticDataDocument,
  ViewsDocument,
  type ViewsQuery,
} from '../../../generated/graphql'
import { useStore } from 'vuex'
import type { Get } from 'type-fest'
import { useUserSettings } from '@/composition/useUserSettings'

export type Emits = {
  done: []
}

const emit = defineEmits<Emits>()

/**
 * This could be done in a reactive way via vueuse/useMediaQuery
 * @see https://vueuse.org/core/usemediaquery/
 */
const isMobile = window.matchMedia(`(max-width: ${BREAKPOINT.MOBILE})`).matches
const { client: apolloClient } = useApolloClient()
const store = useStore()
const userSettings = useUserSettings()

const xstate = useXState(PmStartupState, {
  actions: {
    loadInitialData: () => loadInitialData(),
    emitDone: () => emit('done'),
  },
})

onMounted(() => xstate.service.value.send('MOUNTED'))

const isLoading = computed(() => {
  return xstate.state.value.matches('initialLoading')
})

async function loadInitialData() {
  const waitForSetting = getAndApplySettings()
  const waitForUser = getUserAndApplyAbility()
  const waitForStaticData = getStaticData()
  const waitForViews = getViews()

  const settings = await waitForSetting
  const { userNeedsToChangePassword } = await waitForUser
  await waitForStaticData
  const views = await waitForViews

  if (userNeedsToChangePassword) {
    return xstate.service.value.send('CHANGE_PASSWORD')
  }

  applyView({ views, settings })
  setDayWidth()

  return xstate.service.value.send('INITIAL_LOADING_FINISHED')
}

function normalizeRoles(roles: Get<CurrentUserQuery, 'user.roles'>) {
  const result: string[] = []
  if (!roles) return result

  roles.forEach((role) => {
    if (isNil(role)) return
    result.push(role)
  })

  return result
}

async function getUserAndApplyAbility(): Promise<{
  userNeedsToChangePassword: boolean
}> {
  const queryCurrentUser = await apolloClient.query({
    query: CurrentUserDocument,
    fetchPolicy: 'network-only',
  })

  const user = queryCurrentUser.data.user
  if (!user)
    return {
      userNeedsToChangePassword: false,
    }

  addUserAbility({
    roles: normalizeRoles(user.roles),
    easyJobRoles: normalizeRoles(user.easyJobRoles),
    addressId: user.address?.id,
  })

  return {
    userNeedsToChangePassword:
      queryCurrentUser.data?.user?.changePasswordNextLogin ?? false,
  }
}

async function getAndApplySettings() {
  const settings = await userSettings.load()
  // User does not have any persoplan settings yet
  if (!settings) return

  store.commit('applyPersistedSettings', settings)

  return settings
}

async function getStaticData() {
  await apolloClient.query({
    query: StaticDataDocument,
    fetchPolicy: 'network-only',
  })
}

type View = NonNullable<Get<ViewsQuery, 'views[0]'>>

async function getViews() {
  const viewsQuery = await apolloClient.query({
    query: ViewsDocument,
    fetchPolicy: 'network-only',
  })

  const views: View[] = []

  viewsQuery.data?.views?.forEach((view) => {
    if (isNil(view)) return
    views.push(view)
  })

  return views
}

function applyView(options: { views: View[]; settings: any }) {
  // New users don't have settings, in this case do nothing
  if (!options.settings) return

  const savedViewId = options.settings['view.id']
  const defaultViewMobile = options.settings['view.defaultSavedViewIdMobile']
  const defaultViewDesktop = options.settings['view.defaultSavedViewIdDesktop']

  let viewIdToSearchFor = savedViewId

  if (!viewIdToSearchFor) {
    viewIdToSearchFor = isMobile ? defaultViewMobile : defaultViewDesktop
  }

  const view = options.views.find((view) => view.id === viewIdToSearchFor)
  if (!view) {
    console.warn('view not found')
    return
  }

  store.commit('view/setView', {
    id: view.id,
    title: view.title,
    query: view.query,
  })
}

function setDayWidth() {
  if (!isMobile) return

  const overlap = 40
  const treshold = 500 + overlap

  const showOneDay = window.matchMedia(`(max-width: ${treshold}px)`).matches
  const showTwoDays = window.matchMedia(`(min-width: ${treshold}px)`).matches

  if (showOneDay) {
    store.commit('cssVar/set', {
      name: `dayWidth`,
      value: window.innerWidth - overlap,
    })

    return
  }

  if (showTwoDays) {
    store.commit('cssVar/set', {
      name: `dayWidth`,
      value: (window.innerWidth - overlap) / 2,
    })

    return
  }
}
</script>
